import type { PortableTextBlock } from '@portabletext/types';
import { CLOUDINARY_ASSET_FIELDS } from '../queries/sanity/snippets';
import { cdnClient } from './client';
import { enrichMuxVideosWithPlaceholders } from './portableTextHelpers';

export interface FAQ extends BaseFAQ {
  body: PortableTextBlock;
  category: string;
  displayDate: boolean | null;
  metaDescription: string;
  metaTitle: string;
  relatedFaqs: BaseFAQ[] | null;
}

export interface BaseFAQ {
  title: string;
  slug: string;
}

export interface FeaturedFAQ {
  body: PortableTextBlock;
  title: string;
}

export interface FAQSection {
  title: string;
  questions: { body: PortableTextBlock; title: string; slug: string }[];
}

export const getFaqCategories = `//groq
*[_type == "faqSection" 
  && !(_id in path("drafts.**")) 
] 
  {
    "category": coalesce(category, ""),
  }
`;

export const getFeaturedFaqsPosts = `//groq
*[_type == "faqPost"][isFeatured == true]{
  title,
  "body": body[]{
    ...,
    _type == "cloudinaryAsset" => @{
      ${CLOUDINARY_ASSET_FIELDS}
    },
    markDefs[]{
      ...,
      _type == "internalLink" => {
        "slug": @.item->slug,
      }
    }
  },
}
`;

const formatCategorySlugSection = (category: string): string =>
  category
    .toLowerCase()
    .replace(/\s+/g, '-') // spaces to dashes
    .replace(/&/g, '') // ampersands to and
    .replace(/[^a-zA-Z0-9/_-]/g, '') // completely strip anything that is not a letter, number, -, _, or /
    .replace(/-{2,}/g, '-') // double dashes to one dash
    .slice(0, 200);

export async function getAllFaqCategories(): Promise<BaseFAQ[]> {
  const faqs: { category: string }[] = await cdnClient.fetch(getFaqCategories);
  const faqCategoryNames = new Set(faqs.map((b) => b.category));
  const faqCategories = Array.from(faqCategoryNames)
    .sort()
    .filter((b) => b.length > 0)
    .map((b) => ({
      slug: `/faqs/${formatCategorySlugSection(b)}`,
      title: b,
    }));

  return faqCategories;
}

export async function getFeaturedFaqs(): Promise<FeaturedFAQ[]> {
  const faqs: FeaturedFAQ[] = await cdnClient.fetch(getFeaturedFaqsPosts);

  return Promise.all(
    faqs.map(async (faq) => ({
      ...faq,
      body: await enrichMuxVideosWithPlaceholders(faq.body),
    }))
  );
}

export async function getAllFaqSlugs(): Promise<string[]> {
  return await cdnClient.fetch(`*[_type == "faqPost"][].slug.current`);
}

export async function getFaqSectionsByCategoryName(
  name: string
): Promise<FAQSection[]> {
  const sections: FAQSection[] = await cdnClient.fetch(
    `*[_type == "faqSection" && category == $name]{
      title, 
      questions[]->{
        body,
        "slug": slug.current,
        title,
      }
    }`,
    { name }
  );

  return Promise.all(
    sections.map(async (section) => ({
      ...section,
      questions: await Promise.all(
        section.questions.map(async (question) => ({
          ...question,
          body: await enrichMuxVideosWithPlaceholders(question.body),
        }))
      ),
    }))
  );
}

export async function getFaqBySlug(name: string): Promise<FAQ> {
  const faq: FAQ = await cdnClient.fetch(
    `*[_type == "faqPost" && slug.current == $name][0]{
      dateModified,
      relatedFaqs[]->{
        title,
        "slug": slug.current
      },
      displayDate,
      title,
      "body": body[]{
        ...,
        _type == "cloudinaryAsset" => @{
          ${CLOUDINARY_ASSET_FIELDS}
        },
        markDefs[]{
          ...,
          _type == "internalLink" => {
            "slug": @.item->slug,
          }
        }
      },
      metaTitle,
      metaDescription,
      category,
      "slug": slug.current
    }`,
    { name }
  );

  if (!faq) {
    return faq;
  }

  faq.body = await enrichMuxVideosWithPlaceholders(faq.body);
  return faq;
}
